import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import { Weight } from '@rematter/frontend-lib-api-client';

import { enumLoadWeightType, LoadRecord } from '../types/load';
import { toMoney } from '../utils/money';
import { toWeight } from '../utils/weight';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const loadsTabDetailQueryAtom = createQueryAtomFactory(
  'trade.loadsTabDetail',
  (params: { id: string }) => ({
    load: {
      __args: {
        id: params.id,
      },
      id: true,
      status: true,
      reviewed: true,
      documentNumber: true,
      shippedDate: true,
      salesOrderId: true,
      bookingId: true,
      facilityId: true,
      heavyWeight: {
        value: true,
        unit: true,
      },
      lightWeight: {
        value: true,
        unit: true,
      },
      salesOrder: {
        id: true,
        documentNumber: true,
        counterpartyReferenceNumber: true,
        paymentTerm: true,
        account: {
          id: true,
          name: true,
        },
      },
      booking: {
        id: true,
        documentNumber: true,
      },
      reconciliation: {
        id: true,
        invoiceId: true,
      },
      lineItems: {
        id: true,
        loadId: true,
        type: true,
        itemId: true,
        item: {
          id: true,
          name: true,
          skuNumber: true,
        },
        tagId: true,
        salesOrderLineId: true,
        salesOrderLine: {
          id: true,
        },
        phase: true,
        unitPrice: {
          amount: true,
          currencyCode: true,
        },
        estimatedPrice: {
          amount: true,
          currencyCode: true,
        },
        pricingUnit: true,
        weights: true,
      },
      timestamps: {
        createdAt: true,
      },
    },
  }),
  {
    select: (data): { load: LoadRecord | null } => {
      if (!data?.load) {
        return { load: null };
      }

      return {
        load: {
          id: data.load.id,
          status: data.load.status,
          reviewed: data.load.reviewed ?? false,
          loadNumber: data.load.documentNumber.toString(),
          shippedDate:
            maybeParseCalendarDate(data.load.shippedDate) ?? undefined,
          salesOrderId: data.load.salesOrderId ?? undefined,
          salesOrderNumber: data.load.salesOrder?.documentNumber,
          bookingId: data.load.bookingId ?? undefined,
          bookingNumber: data.load.booking?.documentNumber ?? undefined,
          facilityId: data.load.facilityId ?? undefined,
          accountId: data.load.salesOrder?.account?.id,
          invoiceId: data.load.reconciliation?.invoiceId ?? undefined,
          heavyWeight: data.load.heavyWeight
            ? toWeight(data.load.heavyWeight.value, data.load.heavyWeight.unit)
            : null,
          lightWeight: data.load.lightWeight
            ? toWeight(data.load.lightWeight.value, data.load.lightWeight.unit)
            : null,
          salesOrder: data.load.salesOrder
            ? {
                id: data.load.salesOrder.id,
                documentNumber: data.load.salesOrder.documentNumber,
                customerName: data.load.salesOrder.account.name ?? null,
                counterPartyPurchaseOrderNumber:
                  data.load.salesOrder.counterpartyReferenceNumber ?? null,
                paymentTerm: data.load.salesOrder.paymentTerm ?? undefined,
              }
            : undefined,
          lineItems: data.load.lineItems.map((lineItem) => ({
            id: lineItem.id,
            loadId: lineItem.loadId,
            type: lineItem.type,
            itemId: lineItem.itemId,
            item: lineItem.item ?? null,
            tagId: lineItem.tagId,
            unitPrice: lineItem.unitPrice ? toMoney(lineItem.unitPrice) : null,
            estimatedPrice: lineItem.estimatedPrice
              ? toMoney(lineItem.estimatedPrice)
              : null,
            pricingUnit: lineItem.pricingUnit,
            phase: lineItem.phase,
            weights: lineItem.weights as Record<string, Weight>,
          })),
          lineItemCount: data.load.lineItems.length,
          netRequestedQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.Net
              ]?.value ?? 0),
            0,
          ),
          netOwedQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.QuantityOwed
              ]?.value ?? 0),
            0,
          ),
          netQuantity: data.load.lineItems.reduce(
            (acc, lineItem) =>
              acc +
              ((lineItem.weights as Record<string, Weight>)?.[
                enumLoadWeightType.Net
              ]?.value ?? 0),
            0,
          ),
          reconciliation: null,
          files: [],
          timestamps: {
            createdAt: parseZonedDateTime(data.load.timestamps.createdAt),
          },
        },
      };
    },
  },
);
